import { BOOKING_API_URL, supabase } from '../lib/supabase';
import type { QueueEntry, QueueListResponse } from '../types';
import type {
  DeparturePlayLanguages,
  PlayLanguageMode,
  PlayLanguageOption,
} from '../lib/play-languages';

// Thin wrapper around the booking-queue edge function. All operations require
// an authenticated operator session.

export interface QueueApiError extends Error {
  code: string;
  status: number;
}

function makeError(code: string, status: number, message: string): QueueApiError {
  const err = new Error(message) as QueueApiError;
  err.code = code;
  err.status = status;
  return err;
}

async function callQueueEndpoint(body: Record<string, unknown>): Promise<unknown> {
  const { data: sessionData } = await supabase.auth.getSession();
  const jwt = sessionData.session?.access_token;
  if (!jwt) {
    throw makeError('unauthenticated', 401, 'No active operator session');
  }

  let res: Response;
  try {
    res = await fetch(`${BOOKING_API_URL}/booking-queue`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify(body),
    });
  } catch (e) {
    throw makeError(
      'network_error',
      0,
      e instanceof Error ? e.message : String(e),
    );
  }

  let json: unknown = null;
  try {
    json = await res.json();
  } catch {
    /* empty / non-JSON */
  }

  if (!res.ok) {
    const err = (json ?? {}) as { error?: string; message?: string };
    throw makeError(err.error ?? 'queue_failed', res.status, err.message ?? `HTTP ${res.status}`);
  }
  return json;
}

export async function fetchQueue(locationId: string): Promise<QueueEntry[]> {
  const data = (await callQueueEndpoint({ mode: 'list', location_id: locationId })) as
    | QueueListResponse
    | null;
  return data?.entries ?? [];
}

export async function reorderQueue(queueEntryId: string, newPosition: number): Promise<void> {
  await callQueueEndpoint({
    mode: 'reorder',
    queue_entry_id: queueEntryId,
    new_position: newPosition,
  });
}

/** Scope decides whether a remove/mark-started hits one row or every sibling
 *  of the same departure. 'party' rows share a last_action_group_id so a
 *  follow-up undo restores all of them as one unit. */
export type DequeueScope = 'one' | 'party';

export async function removeFromQueue(
  queueEntryId: string,
  departureId: string,
  scope: DequeueScope,
): Promise<void> {
  await callQueueEndpoint({
    mode: 'remove',
    scope,
    queue_entry_id: queueEntryId,
    departure_id: departureId,
  });
}

export async function markAsStarted(
  queueEntryId: string,
  departureId: string,
  scope: DequeueScope,
): Promise<void> {
  await callQueueEndpoint({
    mode: 'mark_started',
    scope,
    queue_entry_id: queueEntryId,
    departure_id: departureId,
  });
}

export async function undoLastDequeue(locationId: string): Promise<void> {
  await callQueueEndpoint({ mode: 'undo_last', location_id: locationId });
}

export async function enqueueDeparture(departureId: string): Promise<void> {
  await callQueueEndpoint({ mode: 'enqueue', departure_id: departureId });
}

/** Operator confirms/corrects the play language of a departure (lowercase
 *  2-letter code). null clears back to the locale default. */
export async function setPlayLanguage(
  departureId: string,
  language: string | null,
): Promise<void> {
  await callQueueEndpoint({
    mode: 'set_language',
    departure_id: departureId,
    language,
  });
}

/** Per-experience play-language chips for one location, keyed by
 *  experience_id. Enabled languages only, popularity-ordered by the DB.
 *  Cached by lib/play-languages.ts — call that, not this, from components. */
export async function fetchLanguageOptions(
  locationId: string,
): Promise<Record<string, PlayLanguageOption[]>> {
  const data = (await callQueueEndpoint({
    mode: 'language_options',
    location_id: locationId,
  })) as Record<string, PlayLanguageOption[]> | null;
  return data ?? {};
}

/** Group vs per-person state of one departure, plus a row per queued person. */
export async function fetchDepartureLanguages(
  departureId: string,
): Promise<DeparturePlayLanguages> {
  return (await callQueueEndpoint({
    mode: 'departure_languages',
    departure_id: departureId,
  })) as DeparturePlayLanguages;
}

/** Flip the whole departure between one language and one per person.
 *  'per_person' seeds every sibling with the language the party runs now. */
export async function setPlayLanguageMode(
  departureId: string,
  mode: PlayLanguageMode,
): Promise<DeparturePlayLanguages> {
  return (await callQueueEndpoint({
    mode: 'set_language_mode',
    departure_id: departureId,
    language_mode: mode,
  })) as DeparturePlayLanguages;
}

/** One person's language. Implies per-person mode for the whole departure. */
export async function setEntryPlayLanguage(
  queueEntryId: string,
  language: string,
): Promise<DeparturePlayLanguages> {
  return (await callQueueEndpoint({
    mode: 'set_entry_language',
    queue_entry_id: queueEntryId,
    language,
  })) as DeparturePlayLanguages;
}

/** "Even weg": the visitor keeps their spot but dispatch skips the row. */
export async function parkEntry(queueEntryId: string): Promise<void> {
  await callQueueEndpoint({ mode: 'park', queue_entry_id: queueEntryId });
}

export async function unparkEntry(queueEntryId: string): Promise<void> {
  await callQueueEndpoint({ mode: 'unpark', queue_entry_id: queueEntryId });
}

// Subscribes to any change on booking.queue_entries filtered by location.
// onChange should refetch the queue — payloads carry no PII so we don't
// attempt to diff-merge.
export function subscribeToQueueChanges(
  locationId: string,
  onChange: () => void,
): () => void {
  const channel = supabase
    .channel(`queue-realtime-${locationId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'booking',
        table: 'queue_entries',
        filter: `location_id=eq.${locationId}`,
      },
      () => onChange(),
    )
    .subscribe();

  return () => {
    void supabase.removeChannel(channel);
  };
}
